import React from 'react'
import { useTranslation } from 'react-i18next'
import { BaseView, BaseText } from '@/components/base'
import { AppButton } from '@/components'
import { Modal } from '@/components/Modal'
import { Instruction } from '@/components/Instruction'
import {
  depositInstructions,
  DepositInstructionsKey,
} from '@/constants/depositInstructions'
import { currencyUtils } from '@/utils'
import useOffsets from '@/hooks/useOffsets'

const { formatDynamicCurrency } = currencyUtils

interface DepositInstructionsModalProps {
  isOpen: boolean
  onClose: () => void
  minimumDepositAmount?: number
  currencyCode: string
  networkArrivalTime?: number
  isLoading?: boolean
}

export const DepositInstructionsModal: React.FC<
  DepositInstructionsModalProps
> = ({
  isOpen,
  onClose,
  minimumDepositAmount,
  currencyCode,
  networkArrivalTime,
  isLoading = false,
}) => {
  const { t } = useTranslation()
  const offsets = useOffsets()

  const handleDescription = (
    key: DepositInstructionsKey,
    descriptionTranslateFirstKey: string,
    descriptionTranslateSecondKey?: string,
  ) => {
    switch (key) {
      case DepositInstructionsKey.MINIMUM_DEPOSIT:
        return `${t(descriptionTranslateFirstKey, 'Minimum deposit is')} ${formatDynamicCurrency(
          {
            amount: minimumDepositAmount || 0,
            currencyCode: currencyCode,
            showDecimals: true,
          },
        )} ${descriptionTranslateSecondKey ? t(descriptionTranslateSecondKey, 'Deposits below this amount will not be credited to your account.') : ''}`

      case DepositInstructionsKey.PROCESSING_TIME:
        return `${t(descriptionTranslateFirstKey, 'Processing time')}: ${networkArrivalTime || 15} ${descriptionTranslateSecondKey ? t(descriptionTranslateSecondKey, 'minutes') : 'minutes'}`

      default:
        return t(
          descriptionTranslateFirstKey,
          'Make sure you are using the correct network. Deposits on other networks will be lost.',
        )
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} showHandle={true}>
      <BaseView
        gap={30}
        style={{
          paddingBottom: `${offsets.bottom - 12}px`,
        }}
      >
        {/* Title */}
        <BaseView center>
          <BaseText
            fontSize={24}
            fontFamily="semibold"
            color="label.default"
            textAlign="center"
          >
            {t('translation:instructions', 'Instructions')}
          </BaseText>
        </BaseView>

        {/* Instructions List */}
        <BaseView gap={20}>
          {depositInstructions.map(
            ({
              key,
              iconName,
              descriptionTranslateFirstKey,
              descriptionTranslateSecondKey,
            }) => (
              <Instruction
                key={key}
                iconName={iconName}
                description={handleDescription(
                  key,
                  descriptionTranslateFirstKey,
                  descriptionTranslateSecondKey,
                )}
                isLoading={isLoading}
              />
            ),
          )}
        </BaseView>

        {/* OK Button */}
        <BaseView px={6}>
          <AppButton
            type="secondaryGray"
            label={t('form:actions.okay', 'Okay')}
            onClick={onClose}
          />
        </BaseView>
      </BaseView>
    </Modal>
  )
}
